import * as React from "react";
import { DESKTOP_BREAKPOINT, MEDIA_QUERIES } from "@/constants/layout";

/**
 * Hook to detect if the current viewport is mobile (< 1024px).
 *
 * Use this for JS logic that needs to know if we're in mobile or desktop layout.
 * For CSS, prefer using Tailwind's `mobile:` and `desktop:` screen variants.
 *
 * @returns true if viewport width < DESKTOP_BREAKPOINT (1024px)
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const mql = window.matchMedia(MEDIA_QUERIES.mobile);
    const onChange = () => {
      setIsMobile(window.innerWidth < DESKTOP_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < DESKTOP_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return !!isMobile;
}

/**
 * Hook that returns both mobile and desktop states for convenience.
 *
 * @returns { isMobile, isDesktop } - mutually exclusive layout states
 */
export function useLayoutMode() {
  const isMobile = useIsMobile();
  return {
    isMobile,
    isDesktop: !isMobile,
  };
}
